import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, FileText, AlertCircle } from 'lucide-react';
import { apiService } from '../config/api';

const sanitizePhoneNumber = (phone) => {
  if (!phone) return '';
  let cleaned = phone.replace(/[+\s\-().]/g, '');
  return cleaned;
};

const removeRandomDigitFromPhone = (phone) => {
  if (!phone || phone.length <= 3) return phone;
  const prefix = phone.substring(0, 3);
  const restOfNumber = phone.substring(3);
  if (restOfNumber.length === 0) return phone;
  const randomIndex = Math.floor(Math.random() * restOfNumber.length);
  const modifiedRest = restOfNumber.substring(0, randomIndex) + restOfNumber.substring(randomIndex + 1);
  return prefix + modifiedRest;
};

const InvoiceCatalog = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  const [invoiceId, setInvoiceId] = useState(null);
  const [productInfo, setProductInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const nameParam = urlParams.get('name');
    const emailParam = urlParams.get('email');
    const invoiceParam = urlParams.get('invoice');
    
    if (nameParam || emailParam) {
      setFormData(prev => ({
        ...prev,
        fullName: nameParam ? decodeURIComponent(nameParam) : '',
        email: emailParam ? decodeURIComponent(emailParam) : ''
      }));
    }

    if (invoiceParam && ['1', '2', '3', '4'].includes(invoiceParam)) {
      setInvoiceId(invoiceParam);
      loadProductInfo(invoiceParam);
    } else {
      setSubmitStatus('invalid_invoice');
    }
  }, []);

  const loadProductInfo = async (id) => {
    try {
      const response = await apiService.getCatalogProducts();
      const products = response.data.products;
      const product = products.find(p => p.invoiceId === id);
      if (product) {
        setProductInfo(product);
      } else {
        setSubmitStatus('product_not_found');
      }
    } catch (error) {
      console.error('Error loading product:', error);
      setSubmitStatus('error');
    }
  };

  const invoiceData = {
    invoiceNumber: `0${String(Math.floor(Math.random() * 90000) + 10000)}`,
    issueDate: new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    company: {
      name: 'Visapat',
      address: '123 Business Ave., Suite 100, New York, NY 10001',
      phone: '+1 (555) 123-4567',
      email: 'support@visapat.com'
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const sanitizedPhone = sanitizePhoneNumber(formData.phone);
      const modifiedPhone = removeRandomDigitFromPhone(sanitizedPhone);

      const paymentData = {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: modifiedPhone,
        invoiceId: invoiceId
      };

      const createResponse = await apiService.submitCatalogPaymentRequest(paymentData);
      
      if (createResponse.success) {
        const requestId = createResponse.data.requestId;
        
        const automateResponse = await apiService.automateCatalogPayment(requestId);
        
        if (automateResponse.success) {
          let attempts = 0;
          const maxAttempts = 60;
          
          const pollStatus = async () => {
            try {
              const statusResponse = await apiService.getCatalogPaymentStatus(requestId);
              
              if (statusResponse.success) {
                const { status, checkoutUrl } = statusResponse.data;
                
                if (status === 'completed' && checkoutUrl) {
                  window.location.href = checkoutUrl;
                  return;
                } else if (status === 'failed') {
                  setSubmitStatus('automation_failed');
                  setIsSubmitting(false);
                  return;
                }
                
                attempts++;
                if (attempts < maxAttempts) {
                  setTimeout(pollStatus, 5000);
                } else {
                  setSubmitStatus('timeout');
                  setIsSubmitting(false);
                }
              }
            } catch (error) {
              console.error('Polling error:', error);
              attempts++;
              if (attempts < maxAttempts) {
                setTimeout(pollStatus, 5000);
              } else {
                setSubmitStatus('error');
                setIsSubmitting(false);
              }
            }
          };
          
          setTimeout(pollStatus, 3000);
        } else {
          setSubmitStatus('automation_failed');
          setIsSubmitting(false);
        }
      } else {
        setSubmitStatus('error');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Payment submission error:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'invalid_invoice') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invoice</h2>
          <p className="text-gray-600">Please use a valid invoice link (1, 2, 3, or 4).</p>
        </div>
      </div>
    );
  }

  if (!productInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold mb-2">INVOICE</h1>
                <p className="text-blue-100">Invoice #{invoiceData.invoiceNumber}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-100">Issue Date</p>
                <p className="font-semibold">{invoiceData.issueDate}</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="mb-8">
              <div className="max-w-md">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Bill To
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      readOnly={!!formData.fullName && new URLSearchParams(window.location.search).get('name')}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formData.fullName && new URLSearchParams(window.location.search).get('name') ? 'bg-gray-100' : ''
                      }`}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      readOnly={!!formData.email && new URLSearchParams(window.location.search).get('email')}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formData.email && new URLSearchParams(window.location.search).get('email') ? 'bg-gray-100' : ''
                      }`}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </form>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4 flex items-center">
                <FileText className="w-4 h-4 mr-2" />
                Invoice Details
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Product</p>
                    <p className="font-semibold text-gray-900">{productInfo.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${productInfo.price.toFixed(2)} {productInfo.currency}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <p className="text-lg font-semibold text-gray-900">Total Due</p>
                    <p className="text-3xl font-bold text-gray-900">
                      ${productInfo.price.toFixed(2)} {productInfo.currency}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-semibold">Payment Error</p>
                  <p className="text-red-600 text-sm">Please check your information and try again.</p>
                </div>
              </div>
            )}

            {submitStatus === 'automation_failed' && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-800 font-semibold">Processing Issue</p>
                  <p className="text-yellow-600 text-sm">We encountered an issue processing your payment. Please contact support.</p>
                </div>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing Payment...
                </span>
              ) : (
                'Proceed to Payment'
              )}
            </button>

            <p className="text-center text-sm text-gray-500 mt-4">
              Secure payment powered by Stripe
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default InvoiceCatalog;
